import * as THREE from "three";

const LABEL_COLOR = 0xe53935;
const LABEL_HEX = "#e53935";

/**
 * @param {string|number} text
 * @param {number} worldSize
 */
function makeTextSprite(text, worldSize) {
  const canvas = document.createElement("canvas");
  const size = 128;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = LABEL_HEX;
  ctx.font = "bold 72px Arial, Helvetica, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(text), size / 2, size / 2);

  const map = new THREE.CanvasTexture(canvas);
  map.needsUpdate = true;
  const mat = new THREE.SpriteMaterial({
    map,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(worldSize, worldSize, 1);
  sprite.renderOrder = 10;
  return sprite;
}

/**
 * Dimension bracket: main segment + end ticks, with centered numeric sprite.
 * @param {THREE.Group} group
 * @param {THREE.Vector3} a
 * @param {THREE.Vector3} b
 * @param {THREE.Vector3} tickDir - unit vector for end ticks
 * @param {string|number} label
 * @param {number} labelSize
 * @param {THREE.Vector3} [labelOffset]
 */
function addDimension(group, a, b, tickDir, label, labelSize, labelOffset) {
  const color = LABEL_COLOR;
  const tickLen = labelSize * 0.35;
  const tick = tickDir.clone().normalize().multiplyScalar(tickLen);

  const points = [a, b];
  const geo = new THREE.BufferGeometry().setFromPoints(points);
  group.add(
    new THREE.Line(geo, new THREE.LineBasicMaterial({ color, depthTest: false }))
  );

  const t0a = a.clone().sub(tick);
  const t0b = a.clone().add(tick);
  const t1a = b.clone().sub(tick);
  const t1b = b.clone().add(tick);
  group.add(
    new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([t0a, t0b]),
      new THREE.LineBasicMaterial({ color, depthTest: false })
    )
  );
  group.add(
    new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([t1a, t1b]),
      new THREE.LineBasicMaterial({ color, depthTest: false })
    )
  );

  const mid = a.clone().add(b).multiplyScalar(0.5);
  if (labelOffset) mid.add(labelOffset);
  const sprite = makeTextSprite(label, labelSize);
  sprite.position.copy(mid);
  group.add(sprite);
}

/**
 * Red packing dimension labels matching the reference style:
 * - totals per axis (height / width / length in box counts)
 * - per-step plateau width (columns) and height (levels)
 *
 * @param {THREE.Group} group
 * @param {import("./packing/types").ContainerPackResult} container
 */
export function addPackingDimensionLabels(group, container) {
  const o = container?.orientation;
  const heights = container?.columnHeights || [];
  if (!o || !heights.length) return;

  const boxW = o.boxW;
  const boxH = o.boxH;
  const boxD = o.boxD;
  const nx = o.nx || 1;
  const unit = Math.min(boxW, boxH, boxD) || 1;
  const pad = unit * 0.25;
  const labelSize = unit * 0.9;

  const maxH = Math.max(0, ...heights);
  if (maxH <= 0) return;

  let firstCol = -1;
  let lastCol = -1;
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] > 0) {
      if (firstCol < 0) firstCol = c;
      lastCol = c;
    }
  }
  if (firstCol < 0) return;

  const lengthCols = lastCol - firstCol + 1;
  const stackW = nx * boxW;
  const stackH = maxH * boxH;
  const stackZ0 = firstCol * boxD;
  const stackZ1 = (lastCol + 1) * boxD;

  // --- Totals per axis ---
  // Height (Y): left of stack at front
  addDimension(
    group,
    new THREE.Vector3(-pad, 0, stackZ0),
    new THREE.Vector3(-pad, stackH, stackZ0),
    new THREE.Vector3(0, 0, 1),
    maxH,
    labelSize,
    new THREE.Vector3(-labelSize * 0.55, 0, 0)
  );

  // Width / depth (X): along top front edge
  addDimension(
    group,
    new THREE.Vector3(0, stackH + pad, stackZ0),
    new THREE.Vector3(stackW, stackH + pad, stackZ0),
    new THREE.Vector3(0, 0, 1),
    nx,
    labelSize,
    new THREE.Vector3(0, labelSize * 0.45, 0)
  );

  // Length (Z): along bottom, outside left face
  addDimension(
    group,
    new THREE.Vector3(-pad, -pad, stackZ0),
    new THREE.Vector3(-pad, -pad, stackZ1),
    new THREE.Vector3(0, 1, 0),
    lengthCols,
    labelSize,
    new THREE.Vector3(-labelSize * 0.55, 0, 0)
  );

  // --- Per-step / layer labels from contiguous plateaus ---
  const steps =
    container.steps?.length > 0
      ? container.steps
      : stepsFromHeights(heights);

  for (const step of steps) {
    if (step.heightBoxes <= 0 || step.numCols <= 0) continue;
    const z0 = step.startCol * boxD;
    const z1 = (step.startCol + step.numCols) * boxD;
    const yTop = step.heightBoxes * boxH;

    // Plateau width (columns along length) on top of the step
    addDimension(
      group,
      new THREE.Vector3(stackW * 0.5, yTop + pad * 0.6, z0),
      new THREE.Vector3(stackW * 0.5, yTop + pad * 0.6, z1),
      new THREE.Vector3(1, 0, 0),
      step.numCols,
      labelSize * 0.85,
      new THREE.Vector3(0, labelSize * 0.4, 0)
    );

    // Plateau height on the outer (right / back) face of the step
    addDimension(
      group,
      new THREE.Vector3(stackW + pad * 0.5, 0, z1 - boxD * 0.5),
      new THREE.Vector3(stackW + pad * 0.5, yTop, z1 - boxD * 0.5),
      new THREE.Vector3(0, 0, 1),
      step.heightBoxes,
      labelSize * 0.85,
      new THREE.Vector3(labelSize * 0.5, 0, 0)
    );
  }
}

function stepsFromHeights(heights) {
  const steps = [];
  let i = 0;
  while (i < heights.length) {
    const h = heights[i];
    if (h <= 0) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < heights.length && heights[j] === h) j += 1;
    steps.push({ startCol: i, numCols: j - i, heightBoxes: h });
    i = j;
  }
  return steps;
}
